import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

// Efecto "wow": debajo de "ARMANDO TU IMPLEMENTACIÓN…" corre una consola que
// va "programando" el proceso (log + pseudo-código), para que se sienta que se
// está construyendo un montón de cosas. Loopea, así nunca se queda vacío.
const POOL = [
  "$ olimpo build --proceso",
  "› analizando tu proceso…",
  "const pasos = leerPasos(proceso)",
  "› mapeando responsables y herramientas",
  "detectarForma(pasos) → evaluando 7 formas",
  "✓ forma elegida",
  "› separando lo que se saca de encima",
  "reglas.map(r => validar(r))",
  "› redactando el pedido para tu IA",
  "generarDocumento({ contexto, pasos, reglas })",
  "› pasando el candado de seguridad…",
  "✓ candado puesto",
  "› puliendo los criterios de éxito",
  "escribirMarkdown(secciones)",
  "› ordenando el paso a paso",
  "› revisando cada línea…",
  "casi listo",
];

const MAX_VISIBLE = 8;

function colorDe(linea) {
  if (linea.startsWith("$")) return "#C9A84C";
  if (linea.startsWith("✓")) return "#C9A84C";
  if (linea.startsWith("›")) return "#8C8477";
  return "#E9E1D2"; // código
}

export default function GenerandoImplementacion({ paciencia = false }) {
  const [lineas, setLineas] = useState([]); // líneas completas
  const [actual, setActual] = useState(""); // línea tipeándose
  const idx = useRef(0);

  useEffect(() => {
    let cancel = false;
    let t = null;

    const tipear = (texto, i = 0) => {
      if (cancel) return;
      if (i <= texto.length) {
        setActual(texto.slice(0, i));
        t = setTimeout(() => tipear(texto, i + 1), 22);
      } else {
        setLineas((prev) => {
          const next = [...prev, texto];
          return next.length > MAX_VISIBLE ? next.slice(next.length - MAX_VISIBLE) : next;
        });
        setActual("");
        idx.current = (idx.current + 1) % POOL.length;
        t = setTimeout(() => tipear(POOL[idx.current]), 320);
      }
    };

    tipear(POOL[idx.current]);
    return () => { cancel = true; if (t) clearTimeout(t); };
  }, []);

  const glow = 132;
  const ring = 100;

  return (
    <div className="min-h-screen bg-negro flex flex-col items-center justify-center px-6 gap-10" data-testid="generando-implementacion">
      {/* Casco + glow (mismo lenguaje visual que LoaderOlimpo) */}
      <div className="flex flex-col items-center gap-5">
        <div className="relative flex items-center justify-center" style={{ width: glow, height: glow }}>
          <motion.div
            className="absolute rounded-full"
            style={{ width: glow, height: glow, background: "radial-gradient(circle, rgba(201,168,76,0.32) 0%, rgba(201,168,76,0) 68%)" }}
            animate={{ scale: [1, 1.22, 1], opacity: [0.45, 0.9, 0.45] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute rounded-full"
            style={{
              width: ring, height: ring,
              background: "conic-gradient(from 0deg, rgba(201,168,76,0) 0%, rgba(255,217,76,0.95) 22%, rgba(201,168,76,0) 52%)",
              WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
            }}
            animate={{ rotate: 360 }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "linear" }}
          />
          <motion.img
            src="/olimpo-helmet.png"
            alt="Olimpo"
            style={{ height: 54, width: "auto", filter: "drop-shadow(0 0 14px rgba(201,168,76,0.55))" }}
            animate={{ scale: [1, 1.07, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>
        <div className="mono-label text-crema tracking-[0.2em]" data-testid="loader-olimpo-texto">ARMANDO TU IMPLEMENTACIÓN…</div>
        {paciencia && (
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
            className="text-gris text-sm text-center max-w-sm leading-relaxed"
            data-testid="gen-paciencia"
          >
            Esto a veces tarda un par de minutos. Sigo trabajando, no cierres.
          </motion.p>
        )}
      </div>

      {/* Consola "programando" */}
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="w-full max-w-lg rounded-xl overflow-hidden"
        style={{ border: "1px solid rgba(201,168,76,0.45)", background: "#14120E", boxShadow: "0 24px 70px rgba(0,0,0,0.6), 0 0 0 1px rgba(201,168,76,0.05)" }}
        data-testid="generando-consola"
      >
        <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: "1px solid rgba(201,168,76,0.22)", background: "rgba(201,168,76,0.07)" }}>
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#6b6456" }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#6b6456" }} />
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: "#C9A84C" }} />
          <span className="ml-2 text-xs tracking-widest" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#8c8474" }}>olimpo · build</span>
        </div>
        <div className="px-5 py-4 h-[220px] flex flex-col justify-end" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 14, lineHeight: "1.7" }}>
          {lineas.map((l, i) => (
            <div key={`${i}-${l}`} style={{ color: colorDe(l), opacity: 0.7 + Math.min(i, MAX_VISIBLE) * 0.035 }}>{l}</div>
          ))}
          <div style={{ color: colorDe(actual || "code") }}>
            {actual}
            <motion.span
              aria-hidden
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.7, repeat: Infinity }}
              style={{ display: "inline-block", width: 8, marginLeft: 2, color: "#C9A84C" }}
            >▍</motion.span>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
